<template lang="html">
  <transition name="slide-fade">
    <div
      v-show="isVisible"
      v-tracking-context="{ content_element: 'rating' }"
      class="fsd-rating"
      :class="{ 'fsd-rating--active': isOpen }"
    >
      <div
        v-show="!isDone"
        v-click-track="{
          event_element: 'rating_toggle',
          action_type: `rating_toggle_${isOpen ? 'on' : 'off'}`
        }"
        class="fsd-rating__toggle"
        @click.prevent="onToggle"
      >
        <transition name="toggle-fade" mode="out-in">
          <div v-if="!isOpen" key="hint" class="fsd-rating__toggle-hint">
            <p class="text-hint">
              {{ 'client.rating.toggle_hint' | t }}
            </p>
            <svg-icon
              :icon-name="`${isOpen ? 'chevron-right' : 'chevron-left'}`"
            />
          </div>
          <div v-else key="noHint">
            <svg-icon
              :icon-name="`${isOpen ? 'chevron-right' : 'chevron-left'}`"
            />
          </div>
        </transition>
      </div>

      <div
        v-show="isOpen && !isDone"
        class="fsd-rating__content"
        @click.prevent="tagEvent"
      >
        <div class="fsd-rating__headline">
          {{ 'client.rating.headline' | t }}
        </div>

        <div
          class="fsd-rating__score"
          :class="{ 'fsd-rating__score--active': selectedRating }"
        >
          <div
            v-for="rating in maxPoints"
            :key="rating"
            v-click-track="{
              search_context: searchContext,
              event_element: 'rating_icon',
              event_element_pos: '' + rating,
              action_type: 'select',
              target: 'star_' + rating
            }"
            class="fsd-rating__icon-wrapper"
            :class="{
              'fsd-rating__icon-wrapper--active': selectedRating === rating
            }"
            @click.prevent="onSelectRating($event, rating)"
          >
            <svg-icon icon-name="rating-star" />
          </div>
        </div>
        <button
          v-click-track="{
            event_element: 'rating_confirm',
            action_type: 'rating_confirm',
            search_context: searchContext,
            target: 'star_' + selectedRating
          }"
          class="button button--primary fsd-rating__button"
          :disabled="!selectedRating"
          @click.prevent="onSend"
        >
          {{ 'client.rating.send' | t }}
        </button>
      </div>
      <transition name="toggle-fade">
        <div v-show="isDone" class="fsd-rating__splash">
          <span>
            {{ 'client.rating.splash' | t }}
          </span>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import Cookies from 'js-cookie'
import { mapState } from 'vuex'

const COOKIE_UNELIGIBLE = 'rating_status'
const RATING_PAUSED = 'paused'
const RATING_DONE = 'done'

export default {
  components: {},
  props: {
    maxPoints: { type: Number, default: 5 },
    timeoutFadeOut: { type: Number, default: 2 },
    timeoutOpen: { type: Number, default: 10 },
    donePeriod: { type: Number, default: 28 },
    pausedPeriod: { type: Number, default: 7 }
  },
  data: () => ({
    selectedRating: null,
    // Element is visible at all
    isVisible: false,
    // Element is extended with the rating stars/thank-you-text visible
    isOpen: false,
    // User already finished rating within the last `donePeriod`
    isDone: false,
    // User paused rating by blur or manual closing
    isPaused: false,
    // Is the registration fence currently opened
    waitForModal: false
  }),
  computed: {
    ...mapState(['resultsLoading', 'usedSearchMapping', 'searchContext'])
  },
  watch: {
    resultsLoading: {
      handler(resultsLoading) {
        // Wait for results to be available before opening the element
        if (!resultsLoading && this.usedSearchMapping) {
          this.readStatusFromCookie()
          if (this.isDone) {
            return
          }

          if (!this.$isServer) {
            setTimeout(() => {
              this.isVisible = true
            })

            setTimeout(() => this.triggerOpen(), this.timeoutOpen * 1000)
          }
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Listen for registration fence events so that we can wait until the modal is closed
    EventBus.on('registration_fence:opened', () => {
      this.waitForModal = true
    })
    EventBus.on('registration_fence:closed', () => {
      setTimeout(() => this.triggerOpen(), this.timeoutOpen * 1000)
      this.waitForModal = false
    })
    this.$VueEventBus.on('globalBlur', this.onGlobalBlur)
  },
  destroyed() {
    this.$VueEventBus.off('globalBlur', this.onGlobalBlur)
  },
  methods: {
    readStatusFromCookie() {
      const status = Cookies.get(COOKIE_UNELIGIBLE)
      this.isDone = status === RATING_DONE
      this.isPaused = status === RATING_PAUSED
    },
    triggerOpen() {
      // With the modal open, we skip opening and wait for the closed event
      if (this.waitForModal) {
        return
      }

      this.readStatusFromCookie()
      this.isOpen = this.isOpen || !this.isPaused
    },
    close() {
      this.visible = false
      this.updateEligibility()
    },
    pause() {
      this.cookieSet(RATING_PAUSED)
      this.readStatusFromCookie()
    },
    done() {
      this.cookieSet(RATING_DONE)
      this.readStatusFromCookie()
    },
    cookieSet(status) {
      // Explicitly read cookies again to make sure we don't overwrite the cookie if the user has already completed
      // the rating in another tab
      if (
        status === RATING_PAUSED &&
        Cookies.get(COOKIE_UNELIGIBLE) === RATING_DONE
      ) {
        return
      }

      const expires =
        status === RATING_PAUSED ? this.pausedPeriod : this.donePeriod
      Cookies.set(COOKIE_UNELIGIBLE, status, { expires, path: '/' })
    },
    onToggle($event) {
      this.tagEvent($event)
      if (this.isOpen) {
        this.pause()
        this.isOpen = false
      } else {
        this.isOpen = true
      }
    },
    onSelectRating($event, rating) {
      this.tagEvent($event)
      this.selectedRating = rating
    },
    onSend($event) {
      this.tagEvent($event)
      this.done()
      setTimeout(() => {
        this.isVisible = false
      }, this.timeoutFadeOut * 1000)
    },
    onGlobalBlur($event) {
      if (!$event.ratingModal && this.isOpen) {
        this.onToggle($event)
      }
    },
    tagEvent($event) {
      // We'll tag the click event so we don't blur on our own event
      $event.ratingModal = true
    }
  }
}
</script>
<style lang="scss" scoped>
.hidden-transition-leave-active,
.hidden-transition-leave-to {
  display: none;
}
.toggle-fade-leave-active,
.toggle-fade-enter-active {
  transition: all 0.3s ease;
}

.toggle-fade-leave-to,
.toggle-fade-enter {
  opacity: 0;
}

.slide-fade-leave-active,
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-to,
.slide-fade-enter {
  transform: translateX(20px);
  opacity: 0;
}
</style>
